import { ApolloProvider } from '@apollo/client';
import React, { Suspense, lazy, useEffect } from 'react';
import { hot } from 'react-hot-loader/root';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import {
  createTheme,
  Spinner,
  SpinnerSize,
  ToastProvider,
  ToastContent,
  GlobalStyles as GDSGlobalStyles,
  useModalState,
} from '@bechtel/gds';
import {
  ConfigProvider,
  ErrorBoundary,
  ClearToast,
  Content,
  NetworkStatus,
  AppUserProvider,
  AuthRoute,
  ProjectListing,
  Settings,
  EnvProvider,
  NotFound,
} from '@bechtel/client-common';
import { Nav } from '~/components/organisms/nav';
import { client } from './apollo-client';
import { ApplicationGlobalStyles } from '~/components/global-styles';
import { SubscriptionManager } from '~/components/atoms/subscription-manager';
import { I18nProvider } from '@lingui/react';
import { i18n } from '@lingui/core';
import { defaultLocale, dynamicActivate } from './internationalization';
import { envConfig } from './env-config';
// NOTE: always async load route components except initial entry route
const Auth = lazy(() => import('./views/auth'));
const WorkPackages = lazy(() => import('@bechtel/client-work-packages'));
const Blocks = lazy(() => import('@bechtel/client-blocks'));
const Planning = lazy(() => import('@bechtel/client-planning'));
const Timesheet = lazy(() => import('@bechtel/client-timesheet'));
const CrewChange = lazy(() => import('@bechtel/client-crew-change'));
const ProjectAdmin = lazy(() => import('@bechtel/client-project-admin'));
const DashboardView = lazy(() => import('@bechtel/client-dashboard'));
const ReportView = lazy(() => import('@bechtel/client-reports'));
const theme = createTheme();

const AuthenticatedApp = () => {
  const { close, toggle, visible } = useModalState(false);
  const { close: settingsClose, toggle: settingsOpen, visible: settingsVisible } = useModalState(false);

  //Note: Open one of the drawer either settings/project picker on the top.
  useEffect(() => {
    if (visible) settingsClose();
  }, [visible]);

  useEffect(() => {
    if (settingsVisible) close();
  }, [settingsVisible]);
  const handleViewProfile = () => settingsOpen();

  const handleLanguageSelect = (lang) => {
    dynamicActivate(lang.locale);
  };
  return (
    <ErrorBoundary>
      <SubscriptionManager>
        <ToastProvider>
          <GDSGlobalStyles />
          <ApplicationGlobalStyles />
          <Nav toggle={toggle} onViewProfile={handleViewProfile} />
          <ClearToast />
          <Content>
            <ErrorBoundary>
              <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                <Routes>
                  <Route element={<AuthRoute />}>
                    <Route index element={<DashboardView />} />
                    <Route path="work-packages/*" element={<WorkPackages />} />
                    <Route path="blocks" element={<Blocks />} />
                    <Route path="planning" element={<Planning />} />
                    <Route path="timesheet/*" element={<Timesheet />} />
                    <Route path="crew-change/*" element={<CrewChange />} />
                    <Route path="project-admin/*" element={<ProjectAdmin />} />
                    <Route path="reports/*" element={<ReportView />} />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
                {visible && <ProjectListing close={close} />}
                {settingsVisible && <Settings close={settingsClose} onSelectLanguage={handleLanguageSelect} />}
              </Suspense>
              <NetworkStatus />
              <ToastContent />
            </ErrorBoundary>
          </Content>
        </ToastProvider>
      </SubscriptionManager>
    </ErrorBoundary>
  );
};

const App = () => {
  useEffect(() => {
    // With this method we dynamically load the catalogs
    dynamicActivate(defaultLocale);
  }, []);
  return (
    <ErrorBoundary>
      <EnvProvider envConfig={envConfig}>
        <ApolloProvider client={client}>
          <I18nProvider i18n={i18n}>
            <ThemeProvider theme={theme}>
              <AppUserProvider>
                <ConfigProvider>
                  <Router>
                    <ErrorBoundary>
                      <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                        <Routes>
                          <Route path="*" element={<AuthenticatedApp />} />
                          <Route path="auth/*" element={<Auth />} />
                        </Routes>
                      </Suspense>
                    </ErrorBoundary>
                  </Router>
                </ConfigProvider>
              </AppUserProvider>
            </ThemeProvider>
          </I18nProvider>
        </ApolloProvider>
      </EnvProvider>
    </ErrorBoundary>
  );
};

export default hot(App);
